import { Helmet } from "react-helmet";
import { Link, useLocation } from "react-router-dom";
import { Clock, RotateCcw, TrendingDown, Upload, Smartphone, Store } from "lucide-react";
import { usePortalLanguage, tLang } from "@/contexts/PortalLanguageContext";
import { organizationSchema, softwareApplicationSchema } from "@/lib/seo/schema";
import LaunchingSoon from "@/components/LaunchingSoon";

const CDN = "https://pub-f22312f1902b4c5b98d85bf07b70d4e9.r2.dev";

// Brand tokens
const NAVY  = "#1A1A2E";
const EMBER = "#FF6B35";
const TEAL  = "#00BFA5";


export default function Index() {
  const { t } = usePortalLanguage();
  const { pathname } = useLocation();
  const locale = pathname.startsWith("/fr") ? "fr" : pathname.startsWith("/es") ? "es" : "en";

  // Locale-aware paths for the secondary links in the CTA strip below.
  // /why-fyrris and /faq have no Spanish page yet, so es falls back to English.
  const localePath = (key: "manufacturers" | "whyFyrris" | "faq") =>
    ({
      manufacturers: { en: "/manufacturers", fr: "/fr/fabricants", es: "/es/fabricantes" },
      whyFyrris: { en: "/why-fyrris", fr: "/fr/pourquoi-fyrris", es: "/why-fyrris" },
      faq: { en: "/faq", fr: "/fr/faq", es: "/faq" },
    })[key][locale];


  // ─── Section data ───────────────────────────────────────────────────────────

  const problems = [
    {
      icon: Clock,
      title: t("home.problems.indecision.title", "Indecision"),
      desc:  t("home.problems.indecision.desc",  "Delays purchases and stalls your sales floor."),
    },
    {
      icon: RotateCcw,
      title: t("home.problems.returns.title", "Second-guessing"),
      desc:  t("home.problems.returns.desc",  "Returns erode margins and tie up your staff."),
    },
    {
      icon: TrendingDown,
      title: t("home.problems.online.title", "Online browsing"),
      desc:  t("home.problems.online.desc",  "Shoppers research online, then buy from a competitor."),
    },
  ];

  const steps = [
    {
      icon: Upload,
      title: t("home.steps.onboard.title", "Onboard your products"),
      desc:  t("home.steps.onboard.desc",  "Upload your catalog to the Fyrris portal. Our team helps with 3D setup."),
    },
    {
      icon: Smartphone,
      title: t("home.steps.visualize.title", "Customers visualize at home"),
      desc:  t("home.steps.visualize.desc", "Shoppers place your fireplaces, grills, and patio products in their space via AR — before ever visiting a showroom."),
    },
    {
      icon: Store,
      title: t("home.steps.visit.title", "They arrive ready to buy"),
      desc:  t("home.steps.visit.desc",  "Geolocation guides them to your nearest location. They walk in informed and confident."),
    },
  ];

  // ─── Render ─────────────────────────────────────────────────────────────────

  return (
    <>
      <Helmet>
        <title>{tLang(locale, "home.meta.title", "Fyrris")}</title>
        <meta name="description" content={tLang(locale, "home.meta.description")} />
        <meta name="keywords" content="HPB retailer, fireplace AR, BBQ grill visualization, patio products AR, augmented reality retail, hearth specialty store, outdoor living AR platform" />
        <meta property="og:title" content={tLang(locale, "home.meta.ogTitle")} />
        <meta property="og:description" content={tLang(locale, "home.meta.ogDescription")} />
        <meta property="og:image" content="https://www.fyrris.com/fyrris-social-banner.png" />
        <meta property="og:type" content="website" />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content={tLang(locale, "home.meta.ogTitle")} />
        <meta name="twitter:description" content={tLang(locale, "home.meta.ogDescription")} />

        {/* Structured data. Kept INSIDE this Helmet on purpose: rendering it
            from a separate <Helmet> (e.g. a <JsonLd> component) added a third
            Helmet instance and made react-helmet 6.x drop this page's localized
            title back to the generic one from AppLayout — the /fr homepage
            regressed to the English title. One Helmet per page avoids that.
            Description follows the page locale so /fr and /es describe the
            company in their own language. No ratings or review counts — see
            src/lib/seo/schema.ts for what is omitted and why. */}
        <script type="application/ld+json">
          {JSON.stringify(
            organizationSchema({ description: tLang(locale, "home.meta.description") })
          )}
        </script>
        <script type="application/ld+json">
          {JSON.stringify(
            softwareApplicationSchema({ description: tLang(locale, "home.meta.description") })
          )}
        </script>
      </Helmet>

      {/* ── 1. HERO ─────────────────────────────────────────────────────────── */}
      <section
        className="relative overflow-hidden min-h-screen flex flex-col justify-center pt-12 pb-20 md:pb-28 px-4 sm:px-6 lg:px-8"
        style={{ backgroundColor: NAVY }}
      >
        {/* Hero image */}
        <img
          src={`${CDN}/hero/Homepage%20Hero.png`}
          alt="Fireplace and patio products displayed in a home living space"
          className="absolute inset-0 w-full h-full object-cover z-0"
          loading="eager"
        />
        {/* Dark overlay */}
        <div className="absolute inset-0 bg-[#1A1A2E]/60 z-[1] pointer-events-none" />

        <div className="relative z-10 w-full max-w-7xl mx-auto">
          <div className="text-center md:text-left max-w-2xl bg-black/50 backdrop-blur-sm rounded-2xl px-8 py-10 md:px-12 md:py-14">
            <p
              className="inline-block text-xs font-semibold tracking-widest uppercase mb-6 px-3 py-1 rounded-full"
              style={{ backgroundColor: "rgba(255,107,53,0.9)", color: "white" }}
            >
              {t("home.hero.eyebrow", "For HPB Retailers")}
            </p>

            <h1 className="text-4xl sm:text-5xl md:text-6xl lg:text-7xl font-bold leading-tight text-white mb-6">
              {t(
                "home.hero.headline",
                "Your customers decide at home. Fyrris brings them to your store."
              )}
            </h1>

            <p className="text-lg md:text-xl text-gray-100 mb-10">
              {t(
                "home.hero.subhead",
                "The AR platform that lets HPB shoppers visualize fireplaces, grills, and patio products in their space — then sends them to your showroom."
              )}
            </p>

            <div className="flex flex-col sm:flex-row gap-4 justify-center md:justify-start items-center md:items-start">
              <Link
                to={locale === "fr" ? "/fr/detaillants/demo" : locale === "es" ? "/es/minoristas/demo" : "/retailers/demo"}
                className="inline-flex items-center justify-center rounded-full px-8 py-3 text-base font-medium text-white whitespace-nowrap transition hover:opacity-90"
                style={{ backgroundColor: EMBER, boxShadow: "0 4px 14px rgba(255,107,53,0.3)" }}
              >
                {t("home.hero.cta", "Get Started")}
              </Link>

              <Link
                to="/how-it-works"
                className="inline-flex items-center justify-center rounded-full border border-gray-600 px-8 py-3 text-base font-medium text-gray-300 whitespace-nowrap transition hover:border-gray-400 hover:text-white"
              >
                {t("home.hero.ctaSecondary", "See how it works")}
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* ── 2. PROBLEM STRIP ─────────────────────────────────────────────────── */}
      <section className="bg-gray-950 py-16 md:py-20 px-4 sm:px-6 lg:px-8">
        <div className="max-w-7xl mx-auto grid md:grid-cols-3 gap-8 md:gap-12">
          {problems.map(({ icon: Icon, title, desc }) => (
            <div key={title} className="flex flex-col items-start gap-3">
              <Icon className="w-8 h-8 flex-shrink-0" style={{ color: EMBER }} />
              <h3 className="text-lg font-semibold text-white">{title}</h3>
              <p className="text-gray-300 text-sm leading-relaxed">{desc}</p>
            </div>
          ))}
        </div>
      </section>

      {/* ── 3. HOW IT WORKS ──────────────────────────────────────────────────── */}
      <section
        id="how-it-works"
        className="py-20 md:py-28 px-4 sm:px-6 lg:px-8"
        style={{
          backgroundColor: NAVY,
          backgroundImage: `radial-gradient(ellipse 60% 40% at 50% 100%, ${EMBER}0d, transparent)`,
        }}
      >
        <div className="max-w-7xl mx-auto">
          <h2 className="text-3xl md:text-4xl font-bold text-white text-center mb-4">
            {t("home.howItWorks.title", "How It Works")}
          </h2>
          <p className="text-center text-gray-300 mb-14 max-w-xl mx-auto">
            {t(
              "home.howItWorks.subtitle",
              "From catalog upload to showroom visit — three steps."
            )}
          </p>

          <div className="grid md:grid-cols-3 gap-8">
            {steps.map(({ icon: Icon, title, desc }, idx) => (
              <div key={title} className="relative flex flex-col gap-5 bg-gray-900 rounded-xl p-6">
                {/* Step number */}
                <div
                  className="w-14 h-14 rounded-full flex items-center justify-center text-white font-bold text-base flex-shrink-0"
                  style={{ backgroundColor: EMBER }}
                >
                  {idx + 1}
                </div>

                <Icon className="w-7 h-7" style={{ color: TEAL }} />

                <h3 className="text-xl font-semibold text-white">{title}</h3>
                <p className="text-gray-300 text-sm leading-relaxed">{desc}</p>

                {/* Connector line (hidden on last) */}
                {idx < steps.length - 1 && (
                  <div
                    className="hidden md:block absolute top-7 left-full w-8 h-px"
                    style={{ backgroundColor: `${EMBER}44` }}
                  />
                )}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── 4. CONSUMER APP ──────────────────────────────────────────────────── */}
      <section className="bg-gray-950 py-20 md:py-28 px-4 sm:px-6 lg:px-8">
        <div className="max-w-7xl mx-auto md:flex md:items-center md:gap-16">
          {/* Text */}
          <div className="flex-1 mb-10 md:mb-0">
            <p
              className="text-xs font-semibold uppercase tracking-widest mb-4"
              style={{ color: TEAL }}
            >
              {t("home.app.eyebrow", "The Fyrris App")}
            </p>

            <h2 className="text-3xl md:text-4xl font-bold text-white mb-6 leading-snug">
              {t("home.app.headline", "The app your customers use to find you.")}
            </h2>

            <p className="text-gray-300 mb-4 leading-relaxed">
              {t(
                "home.app.body1",
                "Free for homeowners. Available on iOS and Android. Shoppers point their camera at any room and see your fireplaces, grills, and patio furniture appear at real scale."
              )}
            </p>
            <p className="text-gray-300 mb-8 leading-relaxed">
              {t(
                "home.app.body2",
                "When they're ready, built-in geolocation surfaces your nearest showroom. Every session can end at your door."
              )}
            </p>

            <Link
              to="/app"
              className="inline-flex items-center justify-center rounded-full px-8 py-3 text-sm font-medium text-white whitespace-nowrap transition hover:opacity-90"
              style={{ backgroundColor: TEAL }}
            >
              {t("home.app.cta", "Get Early Access")}
            </Link>
          </div>

          {/* Store badges are withheld until the apps are live. */}
          <div className="flex-shrink-0 flex flex-col gap-4 items-start md:items-center">
            <LaunchingSoon label={t("download.launchingSoon", "Launching soon")} />
          </div>
        </div>
      </section>

      {/* ── 5. RETAILER CTA STRIP ────────────────────────────────────────────── */}
      <section
        className="py-20 md:py-28 px-4 sm:px-6 lg:px-8"
        style={{ backgroundColor: NAVY }}
      >
        <div className="max-w-2xl mx-auto flex flex-col items-center text-center gap-6">
          <h2 className="text-2xl md:text-3xl font-bold text-white">
            {t("home.ctaStrip.headline", "Ready to grow your showroom?")}
          </h2>
          <p className="text-gray-300 leading-relaxed">
            {t(
              "home.ctaStrip.body",
              "Join a limited first cohort of retailers."
            )}
          </p>
          <Link
            to="/retailers#pricing"
            className="inline-flex items-center justify-center rounded-full px-8 py-3 text-base font-medium text-white whitespace-nowrap transition hover:opacity-90"
            style={{ backgroundColor: EMBER, boxShadow: "0 4px 14px rgba(255,107,53,0.3)" }}
          >
            {t("home.ctaStrip.cta", "See Retailer Plans")}
          </Link>

          {/* Secondary in-content links. The homepage previously linked only to
              /app, /how-it-works, /retailers and /signup in its body, so
              /manufacturers, /why-fyrris and /faq were reachable from the
              footer alone. These give the three pages a real in-content path
              from the site's highest-authority page. */}
          <p className="text-sm text-gray-400 mt-2">
            <Link to={localePath("manufacturers")} className="underline underline-offset-4 hover:text-white">
              {t("home.ctaStrip.linkManufacturers", "Browse manufacturers")}
            </Link>
            {" · "}
            <Link to={localePath("whyFyrris")} className="underline underline-offset-4 hover:text-white">
              {t("home.ctaStrip.linkWhy", "Why Fyrris")}
            </Link>
            {" · "}
            <Link to={localePath("faq")} className="underline underline-offset-4 hover:text-white">
              {t("home.ctaStrip.linkFaq", "FAQ")}
            </Link>
          </p>
        </div>
      </section>
    </>
  );
}
